import { canPayResources } from "../resources/resource-pool";
import {
  getCardInstance,
  getCardInstanceDefinition,
  getCommanderDefinitionForInstance,
  isSetupGameState,
} from "../selectors/card-instances";
import { getTotalArmySize } from "../selectors/garrison";
import type {
  ActionValidationFailure,
  ActionValidationResult,
  DeployCommanderAction,
  GameState,
  ReplaceCommanderAction,
  ResourcePool,
} from "../types";

type CommanderPlacementAction = DeployCommanderAction | ReplaceCommanderAction;

function invalid(
  code: ActionValidationFailure["code"],
  message: string,
  path: string | null = null,
  details: ActionValidationFailure["details"] = {},
): ActionValidationResult {
  return { valid: false, failure: { code, message, path, details } };
}

function resourceDetails(required: ResourcePool, available: ResourcePool) {
  return {
    requiredFood: required.food,
    requiredProduction: required.production,
    requiredTrade: required.trade,
    requiredScience: required.science,
    requiredCulture: required.culture,
    availableFood: available.food,
    availableProduction: available.production,
    availableTrade: available.trade,
    availableScience: available.science,
    availableCulture: available.culture,
  } as const;
}

function validateCommanderContext(
  state: Readonly<GameState>,
  action: CommanderPlacementAction,
): ActionValidationResult {
  if (
    (state.schemaVersion !== 3 && state.schemaVersion !== 4 && state.schemaVersion !== 5) ||
    (state.catalog.schemaVersion !== 3 && state.catalog.schemaVersion !== 4)
  ) {
    return invalid(
      "ACTION_NOT_IMPLEMENTED",
      "Commander deployment requires gameplay-catalog schema 3.",
      "catalog.schemaVersion",
      {
        actionKind: "deployment-action",
        stateSchemaVersion: state.schemaVersion,
        catalogSchemaVersion: isSetupGameState(state) ? state.catalog.schemaVersion : null,
        requiredCatalogSchemaVersion: state.schemaVersion === 5 ? 4 : 3,
      },
    );
  }

  if (
    state.phase !== "command" &&
    !(state.schemaVersion === 5 && state.phase === "action-window")
  ) {
    return invalid(
      "WRONG_PHASE",
      "A Commander may be deployed or replaced only during the Command Phase.",
      "phase",
      {
        currentPhase: state.phase,
        requiredPhase: state.schemaVersion === 5 ? "action-window" : "command",
      },
    );
  }

  if (state.players[action.playerId].actionsUsed.deploymentActions >= 1) {
    return invalid(
      "DEPLOYMENT_ACTION_ALREADY_USED",
      "A player may use only one Deployment Action per turn.",
      `players.${action.playerId}.actionsUsed.deploymentActions`,
      { deploymentActionsUsed: state.players[action.playerId].actionsUsed.deploymentActions },
    );
  }

  const isCurrentReplacementInstance =
    action.type === "replace-commander" &&
    state.lanes[action.laneId].players[action.playerId].commander?.cardInstanceId ===
      action.commanderCardInstanceId;
  if (
    !state.players[action.playerId].hand.includes(action.commanderCardInstanceId) &&
    !isCurrentReplacementInstance
  ) {
    return invalid(
      "CARD_NOT_IN_HAND",
      "The incoming Commander must be in the acting player's hand.",
      `players.${action.playerId}.hand`,
      { commanderCardInstanceId: action.commanderCardInstanceId },
    );
  }

  const instance = getCardInstance(state, action.commanderCardInstanceId);
  if (instance === undefined) {
    return invalid(
      "CARD_INSTANCE_NOT_FOUND",
      "The selected Commander card instance does not exist in this match.",
      "commanderCardInstanceId",
      { commanderCardInstanceId: action.commanderCardInstanceId },
    );
  }

  if (instance.ownerPlayerId !== action.playerId) {
    return invalid(
      "CARD_NOT_OWNED_BY_PLAYER",
      "A player cannot deploy another player's Commander.",
      `cardInstances.${action.commanderCardInstanceId}.ownerPlayerId`,
      {
        playerId: action.playerId,
        ownerPlayerId: instance.ownerPlayerId,
        commanderCardInstanceId: action.commanderCardInstanceId,
      },
    );
  }

  const definition = getCardInstanceDefinition(state, action.commanderCardInstanceId);
  if (definition === undefined) {
    return invalid(
      "CARD_DEFINITION_NOT_FOUND",
      "The selected Commander card instance references an unknown definition.",
      `cardInstances.${action.commanderCardInstanceId}.cardDefinitionId`,
      { cardDefinitionId: instance.cardDefinitionId },
    );
  }

  if (definition.type !== "commander") {
    return invalid(
      "CARD_NOT_COMMANDER",
      "Only a Commander card may be deployed with this action.",
      "commanderCardInstanceId",
      {
        commanderCardInstanceId: action.commanderCardInstanceId,
        cardDefinitionId: definition.id,
        actualCardType: definition.type,
      },
    );
  }

  if (getCommanderDefinitionForInstance(state, action.commanderCardInstanceId) === undefined) {
    return invalid(
      "CARD_DEFINITION_NOT_FOUND",
      "The selected Commander lacks the schema-3 printed gameplay projection.",
      `catalog.cards.${definition.id}`,
      {
        cardDefinitionId: definition.id,
        requiredCatalogSchemaVersion: state.schemaVersion === 5 ? 4 : 3,
      },
    );
  }

  const lane = state.lanes[action.laneId];
  if (lane.result !== "active") {
    return invalid(
      "TARGET_LANE_NOT_ACTIVE",
      "A Commander cannot be deployed into a resolved lane.",
      `lanes.${action.laneId}.result`,
      { laneId: action.laneId, laneResult: lane.result },
    );
  }

  const side = lane.players[action.playerId];
  if (side.status !== "active") {
    return invalid(
      side.status === "empty" ? "TARGET_LANE_HAS_NO_LAND" : "TARGET_LANE_NOT_ACTIVE",
      "A Commander may be deployed only onto the acting player's active Land.",
      `lanes.${action.laneId}.players.${action.playerId}.status`,
      { laneId: action.laneId, laneSideStatus: side.status },
    );
  }

  if (side.land === null) {
    return invalid(
      "TARGET_LANE_HAS_NO_LAND",
      "The target lane has no active Land for this Commander.",
      `lanes.${action.laneId}.players.${action.playerId}.land`,
      { laneId: action.laneId },
    );
  }

  return { valid: true };
}

function validateCapacityAndPayment(
  state: Readonly<GameState>,
  action: CommanderPlacementAction,
): ActionValidationResult {
  const definition = getCommanderDefinitionForInstance(state, action.commanderCardInstanceId);
  if (definition === undefined) {
    return invalid(
      "CARD_DEFINITION_NOT_FOUND",
      "The incoming Commander definition cannot be resolved.",
      "commanderCardInstanceId",
      { commanderCardInstanceId: action.commanderCardInstanceId },
    );
  }

  const totalArmySize = getTotalArmySize(state, action.playerId, action.laneId);
  if (
    state.schemaVersion !== 5 &&
    "armyCapacity" in definition &&
    totalArmySize > definition.armyCapacity
  ) {
    return invalid(
      "COMMANDER_CAPACITY_EXCEEDED",
      "The existing garrison exceeds the incoming Commander's Army Capacity.",
      `lanes.${action.laneId}.players.${action.playerId}.armies`,
      {
        laneId: action.laneId,
        totalArmySize,
        commanderArmyCapacity: definition.armyCapacity,
      },
    );
  }

  const pool = state.players[action.playerId].kingdomPool;
  if (!canPayResources(pool, definition.cost)) {
    return invalid(
      "INSUFFICIENT_KINGDOM_POOL",
      "The Kingdom Pool cannot pay the incoming Commander's exact printed cost.",
      `players.${action.playerId}.kingdomPool`,
      {
        laneId: action.laneId,
        cardDefinitionId: definition.id,
        ...resourceDetails(definition.cost, pool),
      },
    );
  }

  return { valid: true };
}

export function validateDeployCommanderAction(
  state: Readonly<GameState>,
  action: DeployCommanderAction,
): ActionValidationResult {
  const context = validateCommanderContext(state, action);
  if (!context.valid) return context;

  const commander = state.lanes[action.laneId].players[action.playerId].commander;
  if (commander !== null) {
    return invalid(
      "TARGET_LANE_ALREADY_HAS_COMMANDER",
      "Deploy Commander requires an empty Commander slot; use replacement for an occupied lane.",
      `lanes.${action.laneId}.players.${action.playerId}.commander`,
      { laneId: action.laneId, currentCommanderCardInstanceId: commander.cardInstanceId },
    );
  }

  return validateCapacityAndPayment(state, action);
}

export function validateReplaceCommanderAction(
  state: Readonly<GameState>,
  action: ReplaceCommanderAction,
): ActionValidationResult {
  const context = validateCommanderContext(state, action);
  if (!context.valid) return context;

  const currentCommander = state.lanes[action.laneId].players[action.playerId].commander;
  if (currentCommander === null) {
    return invalid(
      "TARGET_LANE_HAS_NO_COMMANDER",
      "Replace Commander requires an existing Commander in the target lane.",
      `lanes.${action.laneId}.players.${action.playerId}.commander`,
      { laneId: action.laneId },
    );
  }

  if (currentCommander.cardInstanceId === action.commanderCardInstanceId) {
    return invalid(
      "COMMANDER_REPLACEMENT_INSTANCE_MISMATCH",
      "The incoming replacement must be a different Commander card instance.",
      "commanderCardInstanceId",
      {
        laneId: action.laneId,
        currentCommanderCardInstanceId: currentCommander.cardInstanceId,
        incomingCommanderCardInstanceId: action.commanderCardInstanceId,
      },
    );
  }

  const currentInstance = getCardInstance(state, currentCommander.cardInstanceId);
  if (currentInstance === undefined) {
    return invalid(
      "CARD_INSTANCE_NOT_FOUND",
      "The current Commander lacks an authoritative card-instance record.",
      `lanes.${action.laneId}.players.${action.playerId}.commander.cardInstanceId`,
      { currentCommanderCardInstanceId: currentCommander.cardInstanceId },
    );
  }
  if (currentInstance.ownerPlayerId !== action.playerId) {
    return invalid(
      "CARD_NOT_OWNED_BY_PLAYER",
      "A player cannot replace another player's Commander.",
      `cardInstances.${currentCommander.cardInstanceId}.ownerPlayerId`,
      {
        playerId: action.playerId,
        ownerPlayerId: currentInstance.ownerPlayerId,
        currentCommanderCardInstanceId: currentCommander.cardInstanceId,
      },
    );
  }

  return validateCapacityAndPayment(state, action);
}
